import { BatchResult } from './batch-result';
import { CoordinatorService } from './coordinator.service';
import { ScriptFilter } from './script-filter';

export class Filter {
    private _valueLs?: [string, number][];
    private _idSet?: Set<string>;
    isOpen = false;
    private _requiredValueSet = new Set<string>();
    private _excludedValueSet = new Set<string>();

    moreSize: number = 0;
    widget: any;

    constructor(
        private coords: CoordinatorService,
        public tag: string,
        public isPreview = false,
    ) {}

    get title() {
        if (this.tag.startsWith('batch-')) {
            const br = this.coords.batch$.getResult(this.tag.slice(6));
            return br?.title || 'B???';
        }
        return this.coords.text(`filter-${this.tag}`);
    }

    get isHidden() {
        return !!this.coords.pin$.misc?.has(`hide-filter-${this.tag}`);
    }

    set isHidden(value: boolean) {
        if (value) {
            this.coords.pin$.misc?.on(`hide-filter-${this.tag}`);
        } else {
            this.coords.pin$.misc?.off(`hide-filter-${this.tag}`);
        }
    }

    private _run_batch() {
        const br = this.coords.batch$.getResult(this.tag.slice(6));
        if (!br) return;
        this.coords.dataset$.current.mainXitemLs.forEach((xitm) => {
            if (this._excludedValueSet.size) {
                for (let eX of this._excludedValueSet) {
                    if (xitm.hasBatch(br, eX)) return;
                }
            }
            if (!this._requiredValueSet.size) {
                this._idSet!.add(xitm.id);
                return;
            }
            for (let rX of this._requiredValueSet) {
                if (xitm.hasBatch(br, rX)) {
                    this._idSet!.add(xitm.id);
                    return;
                }
            }
        });
    }

    private _run_keyword() {
        this.coords.dataset$.current.mainXitemLs.forEach((xitm) => {
            if (this._excludedValueSet.size) {
                for (let eX of this._excludedValueSet) {
                    if (xitm.hasKeyword(eX)) return;
                }
            }
            if (!this._requiredValueSet.size) {
                this._idSet!.add(xitm.id);
                return;
            }
            for (let rX of this._requiredValueSet) {
                if (xitm.hasKeyword(rX)) {
                    this._idSet!.add(xitm.id);
                    return;
                }
            }
        });
    }

    private _run_has() {
        const withSubsetSet = new Set<string>();
        this.coords.view$.viewLs.forEach((view) => {
            view.idSet.forEach((id) => withSubsetSet.add(id));
        });
        this.coords.dataset$.current.mainXitemLs.forEach((xitm) => {
            if (this._excludedValueSet.size) {
                const eX = this._excludedValueSet;
                if (eX.has('doi') && xitm.get('doi')) return;
                if (eX.has('attachment') && xitm.hasAttachment) return;
                if (eX.has('comment') && xitm.hasComment) return;
                if (eX.has('duplicate') && xitm.hasDuplicate) return;
                if (eX.has('subset') && withSubsetSet.has(xitm.id)) return;
            }
            if (!this._requiredValueSet.size) {
                this._idSet!.add(xitm.id);
                return;
            }

            const rX = this._requiredValueSet;
            if (rX.has('doi') && xitm.get('doi')) {
                this._idSet!.add(xitm.id);
                return;
            }
            if (rX.has('attachment') && xitm.hasAttachment) {
                this._idSet!.add(xitm.id);
                return;
            }
            if (rX.has('comment') && xitm.hasComment) {
                this._idSet!.add(xitm.id);
                return;
            }
            if (rX.has('duplicate') && xitm.hasDuplicate) {
                this._idSet!.add(xitm.id);
                return;
            }
            if (rX.has('subset') && withSubsetSet.has(xitm.id)) {
                this._idSet!.add(xitm.id);
                return;
            }
        });
    }

    private _run_doctype() {
        let c = 1;
        this.coords.dataset$.current.mainXitemLs.forEach((xitm) => {
            const ls = xitm.get('publicationType') || [];
            if (xitm.isPatent) ls.push('patent');
            if (this._excludedValueSet.size) {
                for (let eX of this._excludedValueSet) {
                    if (ls.includes(eX)) return;
                }
            }
            if (!this._requiredValueSet.size) {
                this._idSet!.add(xitm.id);
                return;
            }

            for (let x of ls) {
                if (this._requiredValueSet.has(x)) {
                    this._idSet!.add(xitm.id);
                    return;
                }
            }
        });
    }
    run() {
        this._idSet = undefined;
        if (this._requiredValueSet?.size || this._excludedValueSet?.size) {
            this._idSet = new Set<string>();
            if (this.tag === 'has') return this._run_has();
            if (this.tag === 'doctype') return this._run_doctype();
            if (this.tag === 'keyword') return this._run_keyword();
            if (this.tag.startsWith('batch')) return this._run_batch();

            this.coords.dataset$.current.mainXitemLs.forEach((xitm) => {
                let val = xitm.get(this.tag);
                if (!val) return;
                if (!Array.isArray(val)) val = [val];

                if (this._excludedValueSet.size) {
                    for (let v of val) {
                        v = v.toString().toLowerCase();
                        if (this._excludedValueSet.has(v)) {
                            this._idSet!.delete(xitm.id);
                            return;
                        }
                    }
                }
                if (!this._requiredValueSet.size) {
                    this._idSet!.add(xitm.id);
                    return;
                }
                for (let v of val) {
                    v = v.toString().toLowerCase();
                    if (this._requiredValueSet.has(v)) {
                        this._idSet!.add(xitm.id);
                        return;
                    }
                }
            });
        }
    }

    private _prepareVal(val: any): string {
        if (typeof val !== 'string') val = val.toString();
        val = val.split('::')[0];
        val = val.toLowerCase();
        return val;
    }

    setState(val: string, isExcluded = false) {
        val = this._prepareVal(val);
        if (isExcluded) {
            this._excludedValueSet.add(val);
            this._requiredValueSet.delete(val);
        } else {
            this._requiredValueSet.add(val);
            this._excludedValueSet.delete(val);
        }
    }

    clearState(val: string) {
        val = this._prepareVal(val);
        this._requiredValueSet.delete(val);
        this._excludedValueSet.delete(val);
    }

    switchState(val: string, isReverse = false) {
        val = this._prepareVal(val);
        if (this._requiredValueSet.has(val)) {
            this._requiredValueSet.delete(val);
            if (!isReverse) this._excludedValueSet.add(val);
        } else if (this._excludedValueSet.has(val)) {
            this._excludedValueSet.delete(val);
            if (isReverse) this._requiredValueSet.add(val);
        } else {
            if (isReverse) this._excludedValueSet.add(val);
            else this._requiredValueSet.add(val);
        }
        this.run();
        this.coords.filter$.run();
        this.widget?.ref.markForCheck();
    }

    switchTitle(val: string) {
        val = this._prepareVal(val);
        if (this._requiredValueSet.has(val)) return this.coords.text('req');
        if (this._excludedValueSet.has(val)) return this.coords.text('excl');
        return this.coords.text('—');
    }

    iconStyle(val: string) {
        val = this._prepareVal(val);

        if (this._requiredValueSet.has(val)) return `filter-icon-required`;
        if (this._excludedValueSet.has(val)) return `filter-icon-excluded`;
        return `filter-icon-neutral`;
    }

    clear() {
        this._idSet = undefined;
        this._requiredValueSet.clear();
        this._excludedValueSet.clear();
        this.widget?.clear();
    }

    get idSet() {
        return this._idSet;
    }

    get valueLs() {
        if (!this._valueLs) {
            if (!this.coords.project$.isCurrentReady) return [];
            this._valueLs = this.coords.script$
                .dataset(this.isPreview)
                .hitset(this.isPreview)
                .extract(this.tag, true);
        }
        return this._valueLs!;
    }

    setBatchValues(br: BatchResult) {
        this._valueLs = br.filterValueLs;
    }

    get mainLs() {
        const ls = this.valueLs?.slice(0, this.coords.filter$.widgetSize);
        this.moreSize = this.valueLs?.length - ls?.length;
        if (this.moreSize < 0) this.moreSize = 0;

        if (this.moreSize && (this._excludedValueSet.size || this._requiredValueSet.size)) {
            this.valueLs?.slice(this.coords.filter$.widgetSize).forEach((tpl) => {
                const v = tpl[0].toString();
                if (this._excludedValueSet.has(v) || this._requiredValueSet.has(v)) ls.push(tpl);
            });
        }

        return ls.map((tpl) => [tpl[0].toString(), tpl[1]]) as [string, number][];
    }

    dialogLs(term: string) {
        term = term.toString().toLowerCase();
        const ls = this.valueLs
            ?.filter((tpl) =>
                tpl[0]
                    .toString()
                    .toLowerCase()
                    .includes(term || ''),
            )
            .slice(0, this.coords.filter$.dialogSize);
        this.moreSize = this.valueLs?.length - ls?.length;
        if (this.moreSize < 0) this.moreSize = 0;
        return ls;
    }

    save(ls: string[]) {
        if (!this.isActive) return;
        // fb.get("keyword").require("css").exclude("css")

        let txt = `filterBoard.get("${this.tag}")`;
        const r = Array.from(this._requiredValueSet)
            .map((v) => `"${v}"`)
            .join(', ');
        if (r) txt += `.require([${r}])`;
        const e = Array.from(this._excludedValueSet)
            .map((v) => `"${v}"`)
            .join(', ');
        if (e) txt += `.exclude([${e}])`;
        ls.push(txt);
    }

    saveRun(ls: string[]) {
        if (!this.isActive) return;
        const fVar = `filter_${this.tag}`;
        const fVarNot = `filterNot_${this.tag}`;

        if (this._requiredValueSet.size) {
            ls.push(`let ${fVar} = dt.search("${this.tag}", 
                "${Array.from(this._requiredValueSet).join(' or ')}")`);
        } else ls.push(`let ${fVar} = dt.hitset()`);

        if (this._excludedValueSet.size) {
            ls.push(`const ${fVarNot} = dt.search("${this.tag}", 
                "${Array.from(this._excludedValueSet).join(' or ')}")`);
            ls.push(`${fVar} = ${fVar}.not(${fVarNot})`);
        }
        ls.push(`hitsetLs.push(${fVar})`);
    }

    saveHelp() {
        if (!this.isActive) return;
        return {
            tag: this.tag,
            requiredLs: Array.from(this._requiredValueSet),
            excludedLs: Array.from(this._excludedValueSet),
        };
    }

    open() {
        this.isOpen = true;
        if (this.tag === 'has') {
            this._valueLs = undefined;
        }
    }

    close() {
        this.isOpen = false;
    }

    get isActive() {
        return this._idSet !== undefined;
    }

    get hasItems() {
        return true;
    }

    get hasMore() {
        return this.moreSize > 0;
    }

    moreDialog() {
        this.coords.display$.dialog$.next({
            action: 'filter-panel-2',
            filter: this,
        });
    }

    get scriptClass() {
        return new ScriptFilter(this.coords, this);
    }
}
